import { Link } from 'react-router-dom';
import { dashboardMetrics, rfwSponsors, activeAdCampaigns, radioAdProfiles, formatMoney, formatCompactNum } from '@/mocks/rfwAdvertising';

const dm = dashboardMetrics;

const topSponsors = rfwSponsors.slice(0, 6);
const activeCampaigns = activeAdCampaigns.filter(c => c.status === 'activa').slice(0, 4);
const topRadios = [...radioAdProfiles].sort((a, b) => b.radioRevenue - a.radioRevenue).slice(0, 3);

export default function PublicidadSection() {
  return (
    <section className="relative py-16 md:py-24 overflow-hidden bg-background-100">
      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/4 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/3 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold mb-5">
            <i className="ri-money-dollar-circle-line text-xs"></i>
            Publicidad y Patrocinadores
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Tu audiencia{' '}
            <span className="text-amber-600">genera ingresos</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            La publicidad es la mayor fuente de ingresos de RFW. Conectamos radios con marcas, medimos cada campaña y repartimos de forma justa.
          </p>
        </div>

        {/* Dashboard KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-10">
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-amber-600 tabular-nums leading-tight">{formatMoney(dm.totalRadioRevenue)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Ganancia Radios</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-foreground-600 tabular-nums leading-tight">{formatMoney(dm.totalRfwRevenue)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Ganancia RFW</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-emerald-600 tabular-nums leading-tight">{dm.activeSponsors}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Patrocinadores</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center">
            <div className="text-lg md:text-xl font-extrabold text-orange-600 tabular-nums leading-tight">{dm.activeCampaigns}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Campañas Activas</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden sm:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{formatCompactNum(dm.totalImpressions)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Impresiones</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden sm:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{dm.avgCtr}%</div>
            <div className="text-[10px] text-foreground-400 font-medium">CTR Promedio</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden lg:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">${dm.avgRpm}</div>
            <div className="text-[10px] text-foreground-400 font-medium">RPM</div>
          </div>
          <div className="rounded-xl border border-background-200/70 bg-background-50 p-3 text-center hidden lg:block">
            <div className="text-lg md:text-xl font-extrabold text-foreground-800 tabular-nums leading-tight">{formatCompactNum(dm.totalReach)}</div>
            <div className="text-[10px] text-foreground-400 font-medium">Alcance</div>
          </div>
        </div>

        {/* Main 3-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          {/* Revenue Split */}
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
                <i className="ri-pie-chart-2-line text-sm"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">Red Publicitaria</h3>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="text-center">
                <div className="text-2xl font-extrabold text-amber-600 tabular-nums">{dm.radioPercent}%</div>
                <div className="text-[10px] text-foreground-400">Radio</div>
              </div>
              <div className="w-px h-10 bg-background-200"></div>
              <div className="text-center">
                <div className="text-2xl font-extrabold text-foreground-500 tabular-nums">{dm.rfwPercent}%</div>
                <div className="text-[10px] text-foreground-400">RFW</div>
              </div>
            </div>

            <div className="w-full h-4 rounded-full bg-background-200/50 overflow-hidden flex">
              <div className="h-full bg-amber-500 rounded-l-full" style={{ width: `${dm.radioPercent}%` }}></div>
              <div className="h-full bg-foreground-400 rounded-r-full" style={{ width: `${dm.rfwPercent}%` }}></div>
            </div>

            <p className="mt-3 text-[10px] text-foreground-400 text-center leading-relaxed">Configurable · 9 radios monetizando · Pagos mensuales</p>

            {/* Top earning radios */}
            <div className="mt-4 pt-4 border-t border-background-200/70 space-y-2">
              <p className="text-[10px] font-semibold text-foreground-500 mb-2">Radios con más ingresos</p>
              {topRadios.map((r, idx) => (
                <div key={r.radioId} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold tabular-nums flex-shrink-0">{idx + 1}</span>
                    <span className="text-foreground-800 font-medium truncate">{r.radioName}</span>
                  </div>
                  <span className="text-amber-600 font-extrabold tabular-nums whitespace-nowrap">{formatMoney(r.radioRevenue)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Active Sponsors */}
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
                <i className="ri-shake-hands-line text-sm"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">Patrocinadores Activos</h3>
            </div>

            <div className="space-y-2.5">
              {topSponsors.map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100/50 transition-colors cursor-pointer">
                  <div className="w-9 h-9 rounded-lg bg-background-200/70 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-foreground-500">{s.logoInitials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground-950 truncate">{s.name}</p>
                    <p className="text-[10px] text-foreground-500">{s.sector} · {s.activeCampaigns} campañas</p>
                  </div>
                  <span className={`text-[10px] font-semibold capitalize px-1.5 py-0.5 rounded-full whitespace-nowrap ${s.type === 'nacional' ? 'bg-amber-100 text-amber-700' : s.type === 'regional' ? 'bg-emerald-100 text-emerald-700' : 'bg-foreground-100 text-foreground-600'}`}>
                    {s.type}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Active Campaigns */}
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500/10 text-orange-600">
                <i className="ri-megaphone-line text-sm"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">Campañas Destacadas</h3>
            </div>

            <div className="space-y-3">
              {activeCampaigns.map((c) => (
                <div key={c.id} className="p-3 rounded-lg bg-background-100/50 hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-foreground-950 truncate pr-2">{c.name}</p>
                    <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full whitespace-nowrap">Activa</span>
                  </div>
                  <p className="text-[10px] text-foreground-500 mb-2">{c.advertiser} · {c.radioName}</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    <div className="text-center">
                      <div className="text-xs font-extrabold text-foreground-800 tabular-nums">{formatCompactNum(c.impressions)}</div>
                      <div className="text-[8px] text-foreground-400">impr.</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-extrabold text-emerald-600 tabular-nums">{c.ctr}%</div>
                      <div className="text-[8px] text-foreground-400">CTR</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-extrabold text-amber-600 tabular-nums">${c.rpm}</div>
                      <div className="text-[8px] text-foreground-400">RPM</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA + feature strip */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-5 rounded-2xl bg-amber-50/60 border border-amber-200/60">
          <div className="flex items-center gap-6 md:gap-10 flex-wrap justify-center sm:justify-start">
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-flashlight-line text-amber-500 text-base"></i>
              <span>Activación <strong className="text-foreground-950">inmediata</strong></span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-amber-200"></div>
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-shield-check-line text-amber-500 text-base"></i>
              <span>Pagos <strong className="text-foreground-950">mensuales</strong> sin mínimo</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-amber-200"></div>
            <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
              <i className="ri-file-chart-line text-amber-500 text-base"></i>
              <span>Métricas <strong className="text-foreground-950">en tiempo real</strong></span>
            </div>
          </div>
          <Link
            to="/publicidad"
            className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
          >
            Explorar Publicidad
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}